import { useEffect, useState, useCallback } from 'react';
import { Document } from "../../../Models/Document";
import { useParams } from 'react-router-dom';
import {
  Typography,
  Button,
  CircularProgress,
  Box,
  TextField,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useUserContext } from '../../../Context/UserContext';
import { DocumentCompleted } from '../../../Models/DocumentCompleted';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAPIContext } from '../../../Context/APIContext';

export default function ViewDocument() {
  const { apiFetch } = useAPIContext();
  const [source, setSource] = useState<string>("");
  const [document, setDocument] = useState<Document>();
  const [loading, setLoading] = useState<boolean>(false);
  const [completedDocument, setCompletedDocument] = useState<DocumentCompleted>();
  const [userResponse, setUserResponse] = useState<string>("");
  const { documentId } = useParams();
  const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);
  const { fetchPoints } = useUserContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const MIN_CHARS = 200;
  const draftKey = `document-draft-${documentId}`;
  const charCount = userResponse.length;
  const hasText = charCount > 0;
  const isTooShort = hasText && charCount < MIN_CHARS;

  const fetchDocumentData = useCallback(async () => {
    setLoading(true);

    if (documentId) {
      const documentData = await apiFetch<Document>(`Documents/${documentId}`, 'GET', Document.fromJson);
      if (documentData) {
        setDocument(documentData);
        if (documentData.accessUrl) {
          setSource(documentData.accessUrl);
        }
      }

      const documentCompletedData = await apiFetch<DocumentCompleted>(
        `Users/CompletedDocuments/${documentId}`,
        'GET',
        DocumentCompleted.fromJson
      );
      if (documentCompletedData) {
        setCompletedDocument(documentCompletedData);
        setUserResponse(documentCompletedData.submissionText || "");
      }
    }

    setLoading(false);
  }, [apiFetch, documentId]); // ✅ dependencies

  useEffect(() => {
    fetchDocumentData();
  }, [fetchDocumentData]);

  const handleMarkAsComplete = async () => {
    setAwaitingResponse(true);

    const completion = new DocumentCompleted(
      0,
      document?.id || 0,
      "",
      0,
      new Date(),
      document?.points || 0,
      userResponse
    );

    const response = await apiFetch<number>('Users/CompletedDocuments', 'POST', completion);
    if (response && response > 0) {
      completion.id = response;
      setCompletedDocument(completion);
      fetchPoints();
      localStorage.removeItem(draftKey);
    }

    setAwaitingResponse(false);
  };

  const handleOpenInNewTab = () => {
    if (source) {
      window.open(source, '_blank');
    }
  };

  useEffect(() => {
    fetchDocumentData();
  }, [fetchDocumentData]);


    // Load draft from localStorage if not completed
  useEffect(() => {
    if (!completedDocument?.id && documentId) {
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        setUserResponse(savedDraft);
      }
    }
  }, [documentId, completedDocument?.id, draftKey]);

  // Autosave draft to localStorage
  useEffect(() => {
    if (!completedDocument?.id && documentId) {
      localStorage.setItem(draftKey, userResponse);
    }
  }, [userResponse, completedDocument?.id, documentId, draftKey]);

  return (
    <Box sx={{
      maxWidth: '700px',
      pl: { xs: 1, sm: 4 },
      pr: { xs: 1, sm: 2 },
      pt: 0,
      pb: 4
    }}>
      <Box sx={{
        p: { xs: 2, sm: 1 },
      }}>
        {loading ? (
          <Box display="flex" justifyContent="flex-start" p={4}>
            <CircularProgress style={{ color: '#3D9BE9' }} />
          </Box>
        ) : (
          <Stack spacing={3} alignItems="flex-start">
            {/* Title and metadata */}
            <Box width="100%">
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                {document?.title}
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f0f4f0',
                    borderRadius: '5px',
                    padding: '4px 6px',
                    color: '#66bb6a',
                    fontWeight: 'bold',
                  }}
                >                      <ElectricBoltIcon fontSize="small" sx={{ color: '#66bb6a' }} />
                  <Typography variant="body2" fontWeight={600} ml={0.5} color="#66bb6a">
                    {document?.points} points
                  </Typography>
                </Box>
                {!isMobile ? (
                  <Box>
                    <Button
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={handleOpenInNewTab}
                    >
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>View Document In New Tab</Typography>
                    </Button>
                  </Box>
                ) : null}
              </Box>
            </Box>

            {/* Document viewer */}
            <Box sx={{
              position: 'relative',
              width: '100%',
              height: 0,
              paddingTop: '56.25%',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: '#000'
            }}>
              {isMobile ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5'
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleOpenInNewTab}
                    startIcon={<OpenInNewIcon />}
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      px: 3,
                      color: '#FFF',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>View Document</Typography>
                  </Button>
                </Box>
              ) : (
                <iframe
                  src={source}
                  title="Document Viewer"
                  frameBorder="0"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              )}
            </Box>

            {/* Completion form */}
            <Box width="100%">
              <Typography variant="body1" mb={1}>
                Reflect on how this topic will develop your knowledge and skills:
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Suggested OPAL structure: <strong>Overview</strong> · <strong>Points</strong> · <strong>Actions</strong> · <strong>Learning / Links</strong>
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                variant="outlined"
                value={userResponse}
                onChange={(e) => setUserResponse(e.target.value)}
                label="Your reflection"
                disabled={!!completedDocument?.id}
                error={isTooShort}
                helperText={
                  hasText
                    ? `${charCount} / ${MIN_CHARS} characters`
                    : `Minimum ${MIN_CHARS} characters`
                }
                sx={{
                  mb: 2,
                  '& .MuiInputBase-root': {
                    lineHeight: 1.6,
                    backgroundColor: '#fafafa',
                  },
                }}
              />

              {completedDocument?.id ? (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  sx={{
                    backgroundColor: '#66bb6a',
                    fontWeight: 'bold',
                    borderRadius: '5px',
                    py: 1
                  }}
                >
                  Completed
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  disabled={charCount < MIN_CHARS || awaitingResponse}
                  onClick={handleMarkAsComplete}
                  sx={{
                    fontWeight: 600,
                    py: 1,
                    color: '#FFF'
                  }}
                >
                  {awaitingResponse ? "Submitting..." : "Mark as complete"}
                </Button>
              )}
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
}